<template>
  <main class="help-center-page">
    <section class="help-center-hero">
      <div class="help-center-hero__content">
        <span class="help-center-eyebrow">Medhop help center</span>
        <h1>Apa yang bisa kami bantu?</h1>
        <p>Temukan panduan singkat untuk menggunakan fitur utama Medhop.</p>

        <label class="help-center-search">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            v-model.trim="search"
            type="search"
            placeholder="Cari bantuan, fitur, atau pertanyaan..."
            aria-label="Cari bantuan"
          />
          <button v-if="search" type="button" aria-label="Hapus pencarian" @click="search = ''">
            <i class="fas fa-times"></i>
          </button>
        </label>
      </div>

      <div class="help-center-hero__visual" aria-hidden="true">
        <div class="help-center-visual-orbit help-center-visual-orbit--one"></div>
        <div class="help-center-visual-orbit help-center-visual-orbit--two"></div>
        <span class="help-center-visual-icon"><i class="far fa-life-ring"></i></span>
        <span class="help-center-visual-chip help-center-visual-chip--top">
          <i class="fas fa-check-circle"></i> Panduan praktis
        </span>
        <span class="help-center-visual-chip help-center-visual-chip--bottom">
          <i class="fas fa-bolt"></i> Solusi cepat
        </span>
      </div>
    </section>

    <section class="help-category-section">
      <div class="help-section-heading">
        <div>
          <span class="help-center-eyebrow">Jelajahi bantuan</span>
          <h2>Pilih kategori</h2>
          <p>Mulai dari area yang paling sesuai dengan kebutuhan Anda.</p>
        </div>
        <button
          v-if="activeCategory"
          type="button"
          class="help-reset-filter"
          @click="activeCategory = ''"
        >
          Tampilkan semua
        </button>
      </div>

      <div class="help-category-grid">
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          class="help-category-card"
          :class="{ 'help-category-card--active': activeCategory === category.id }"
          @click="activeCategory = activeCategory === category.id ? '' : category.id"
        >
          <span class="help-category-card__icon"><i :class="category.icon"></i></span>
          <span class="help-category-card__content">
            <strong>{{ category.title }}</strong>
            <small>{{ category.description }}</small>
          </span>
          <i class="fas fa-arrow-right help-category-card__arrow"></i>
        </button>
      </div>
    </section>

    <div class="help-center-layout">
      <section class="help-faq-card">
        <div class="help-section-heading">
          <div>
            <span class="help-center-eyebrow">Pertanyaan umum</span>
            <h2>Panduan cepat</h2>
            <p>{{ resultDescription }}</p>
          </div>
          <span class="help-result-count">{{ filteredFaqs.length }} topik</span>
        </div>

        <div v-if="filteredFaqs.length" class="help-faq-list">
          <article
            v-for="faq in filteredFaqs"
            :key="faq.id"
            class="help-faq-item"
            :class="{ 'help-faq-item--open': openFaq === faq.id }"
          >
            <button
              type="button"
              class="help-faq-question"
              :aria-expanded="openFaq === faq.id ? 'true' : 'false'"
              @click="toggleFaq(faq.id)"
            >
              <span class="help-faq-question__icon"><i :class="faq.icon"></i></span>
              <span>{{ faq.question }}</span>
              <i class="fas fa-chevron-down help-faq-question__chevron"></i>
            </button>
            <div v-show="openFaq === faq.id" class="help-faq-answer">
              <p>{{ faq.answer }}</p>
            </div>
          </article>
        </div>

        <div v-else class="help-empty-state">
          <span><i class="fas fa-search"></i></span>
          <h3>Bantuan tidak ditemukan</h3>
          <p>Coba gunakan kata kunci lain atau tampilkan seluruh kategori.</p>
          <button type="button" @click="resetSearch">Reset pencarian</button>
        </div>
      </section>

      <aside class="help-support-card">
        <span class="help-support-card__icon"><i class="fas fa-headset"></i></span>
        <span class="help-center-eyebrow">Butuh bantuan lain?</span>
        <h2>Hubungi tim Wellmagic</h2>
        <p>Tim pengembang siap membantu jika kendala Anda belum terjawab pada panduan.</p>

        <ul>
          <li><i class="fas fa-check"></i> Bantuan penggunaan sistem</li>
          <li><i class="fas fa-check"></i> Kendala teknis aplikasi</li>
          <li><i class="fas fa-check"></i> Informasi layanan Medhop</li>
        </ul>

        <a href="https://wellmagic.id/" target="_blank" rel="noopener noreferrer">
          <span>Kunjungi Wellmagic</span>
          <i class="fas fa-external-link-alt"></i>
        </a>
        <small>Anda akan diarahkan ke situs resmi Wellmagic Media Digital.</small>
      </aside>
    </div>
  </main>
</template>

<script>
export default {
  name: "HelpCenter",

  data() {
    return {
      search: "",
      activeCategory: "",
      openFaq: 1,
      categories: [
        {
          id: "patient",
          title: "Pasien & Rekam Medis",
          description: "Data pasien dan riwayat layanan",
          icon: "fas fa-user-injured",
        },
        {
          id: "appointment",
          title: "Jadwal & Perjanjian",
          description: "Kalender, dokter, dan appointment",
          icon: "far fa-calendar-check",
        },
        {
          id: "billing",
          title: "Billing & Pembayaran",
          description: "Invoice dan transaksi klinik",
          icon: "fas fa-file-invoice-dollar",
        },
        {
          id: "subscription",
          title: "Paket & Langganan",
          description: "Masa aktif dan paket Medhop",
          icon: "fas fa-box-open",
        },
      ],
      faqs: [
        {
          id: 1,
          category: "patient",
          icon: "fas fa-user-plus",
          question: "Bagaimana cara menambahkan pasien baru?",
          answer: "Buka menu Pasien, pilih Daftar Pasien, lalu gunakan tombol Tambah. Lengkapi informasi yang diperlukan dan tekan Simpan.",
        },
        {
          id: 2,
          category: "patient",
          icon: "fas fa-notes-medical",
          question: "Di mana saya dapat melihat rekam medis pasien?",
          answer: "Buka menu Pasien dan pilih Rekam Medis. Gunakan pencarian pasien atau filter yang tersedia untuk menemukan data yang dibutuhkan.",
        },
        {
          id: 3,
          category: "appointment",
          icon: "far fa-calendar-plus",
          question: "Bagaimana membuat perjanjian pasien?",
          answer: "Masuk ke menu Perjanjian, pilih tombol Buat Perjanjian, kemudian tentukan pasien, dokter, tanggal, dan waktu layanan.",
        },
        {
          id: 4,
          category: "appointment",
          icon: "fas fa-filter",
          question: "Bagaimana memfilter kalender berdasarkan dokter?",
          answer: "Pada halaman kalender, gunakan filter Dokter di panel samping. Kalender akan memperbarui jadwal sesuai dokter yang dipilih.",
        },
        {
          id: 5,
          category: "billing",
          icon: "fas fa-receipt",
          question: "Bagaimana melihat detail invoice?",
          answer: "Buka menu Billing, lalu pilih ikon Detail pada invoice yang ingin diperiksa. Status dan rincian pembayaran akan ditampilkan.",
        },
        {
          id: 6,
          category: "subscription",
          icon: "fas fa-crown",
          question: "Bagaimana memperpanjang langganan Medhop?",
          answer: "Buka menu Langganan atau Informasi Klinik, pilih paket dan durasi yang sesuai, lalu lanjutkan ke proses pembayaran.",
        },
      ],
    };
  },

  computed: {
    filteredFaqs() {
      const keyword = this.search.toLowerCase();
      return this.faqs.filter((faq) => {
        const categoryMatches = !this.activeCategory || faq.category === this.activeCategory;
        const keywordMatches = !keyword || `${faq.question} ${faq.answer}`.toLowerCase().includes(keyword);
        return categoryMatches && keywordMatches;
      });
    },

    resultDescription() {
      if (this.search) return `Hasil pencarian untuk “${this.search}”.`;
      if (this.activeCategory) {
        const category = this.categories.find((item) => item.id === this.activeCategory);
        return category ? `Topik pada kategori ${category.title}.` : "Topik bantuan yang sering digunakan.";
      }
      return "Topik bantuan yang sering digunakan.";
    },
  },

  methods: {
    toggleFaq(id) {
      this.openFaq = this.openFaq === id ? null : id;
    },

    resetSearch() {
      this.search = "";
      this.activeCategory = "";
    },
  },
};
</script>

<style lang="scss">
.help-center-page {
  --help-navy: var(--skin-deep);
  --help-blue: var(--skin-primary);
  --help-teal: var(--skin-secondary);
  --help-border: #e3eaf2;
  color: #1a2433;
}

.help-center-eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--help-teal);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.help-center-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  min-height: 220px;
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--help-border);
  border-radius: 20px;
  background: linear-gradient(115deg, rgba(var(--skin-deep-rgb), 0.09), rgba(var(--skin-primary-rgb), 0.06), rgba(var(--skin-secondary-rgb), 0.08));
  box-shadow: 0 10px 28px rgba(var(--skin-deep-rgb), 0.06);
}

.help-center-hero__content {
  display: flex;
  padding: 27px 30px;
  justify-content: center;
  flex-direction: column;
}

.help-center-hero h1 {
  margin: 0 0 7px;
  color: #1a2433 !important;
  font-size: 28px !important;
  font-weight: 700;
  letter-spacing: -0.035em;
}

.help-center-hero p {
  margin: 0 0 17px;
  color: #647185;
  font-size: 12px;
}

.help-center-search {
  display: flex;
  width: min(100%, 570px);
  height: 44px;
  margin: 0;
  padding: 0 13px;
  align-items: center;
  gap: 10px;
  border: 1px solid #d8e2ec;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 8px 20px rgba(var(--skin-deep-rgb), 0.07);
}

.help-center-search > i {
  color: var(--help-teal);
}

.help-center-search input {
  min-width: 0;
  height: 100%;
  flex: 1 1 auto;
  padding: 0;
  color: #1a2433;
  border: 0;
  background: transparent;
  box-shadow: none;
  font-size: 11px;
  outline: 0;
}

.help-center-search button {
  display: grid;
  width: 27px;
  height: 27px;
  padding: 0;
  place-items: center;
  color: #7b8798;
  border: 0;
  border-radius: 8px;
  background: #f1f4f8;
  font-size: 9px;
}

.help-center-hero__visual {
  position: relative;
  display: grid;
  overflow: hidden;
  place-items: center;
  background: linear-gradient(145deg, var(--help-navy), var(--help-blue), var(--help-teal));
}

.help-center-visual-icon {
  position: relative;
  z-index: 2;
  display: grid;
  width: 82px;
  height: 82px;
  place-items: center;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.14);
  box-shadow: 0 18px 38px rgba(8, 31, 66, 0.25);
  font-size: 33px;
}

.help-center-visual-orbit {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50%;
}

.help-center-visual-orbit--one { width: 180px; height: 180px; }
.help-center-visual-orbit--two { width: 260px; height: 260px; }

.help-center-visual-chip {
  position: absolute;
  z-index: 3;
  display: inline-flex;
  padding: 7px 10px;
  align-items: center;
  gap: 6px;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.17);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.13);
  font-size: 8px;
  font-weight: 700;
  backdrop-filter: blur(8px);
}

.help-center-visual-chip--top { top: 28px; right: 22px; }
.help-center-visual-chip--bottom { bottom: 28px; left: 22px; }

.help-category-section,
.help-faq-card,
.help-support-card {
  border: 1px solid var(--help-border);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 9px 25px rgba(var(--skin-deep-rgb), 0.05);
}

.help-category-section {
  margin-bottom: 16px;
  padding: 17px;
}

.help-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.help-section-heading h2,
.help-support-card h2 {
  margin: 0 0 3px;
  color: #1a2433 !important;
  font-size: 16px !important;
  font-weight: 700;
}

.help-section-heading p,
.help-support-card p {
  margin: 0;
  color: #6b7280;
  font-size: 10px;
}

.help-reset-filter,
.help-result-count {
  min-height: 29px;
  padding: 6px 9px;
  color: var(--help-blue);
  border: 1px solid rgba(var(--skin-primary-rgb), 0.15);
  border-radius: 9px;
  background: rgba(var(--skin-primary-rgb), 0.07);
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
}

.help-category-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.help-category-card {
  display: flex;
  min-width: 0;
  min-height: 78px;
  padding: 11px;
  align-items: center;
  gap: 9px;
  color: #1a2433;
  text-align: left;
  border: 1px solid var(--help-border);
  border-radius: 13px;
  background: #fbfcfe;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.help-category-card:hover,
.help-category-card--active {
  border-color: rgba(var(--skin-secondary-rgb), 0.45);
  background: linear-gradient(145deg, #fff, rgba(var(--skin-secondary-rgb), 0.06));
  box-shadow: 0 8px 18px rgba(var(--skin-deep-rgb), 0.07);
  transform: translateY(-1px);
}

.help-category-card__icon {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  color: var(--help-teal);
  border-radius: 11px;
  background: rgba(var(--skin-secondary-rgb), 0.09);
}

.help-category-card__content {
  min-width: 0;
  flex: 1 1 auto;
}

.help-category-card__content strong,
.help-category-card__content small {
  display: block;
}

.help-category-card__content strong {
  margin-bottom: 3px;
  color: var(--help-navy);
  font-size: 10px;
  font-weight: 700;
}

.help-category-card__content small {
  color: #7b8798 !important;
  font-size: 8px;
  line-height: 1.4;
}

.help-category-card__arrow {
  color: #a5afbc;
  font-size: 8px;
}

.help-center-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 16px;
  align-items: start;
}

.help-faq-card,
.help-support-card {
  padding: 17px;
}

.help-faq-list {
  display: grid;
  gap: 7px;
}

.help-faq-item {
  overflow: hidden;
  border: 1px solid var(--help-border);
  border-radius: 12px;
  background: #fff;
}

.help-faq-item--open {
  border-color: rgba(var(--skin-secondary-rgb), 0.32);
  box-shadow: 0 6px 16px rgba(var(--skin-deep-rgb), 0.05);
}

.help-faq-question {
  display: flex;
  width: 100%;
  min-height: 48px;
  padding: 9px 11px;
  align-items: center;
  gap: 9px;
  color: #29394f;
  text-align: left;
  border: 0;
  background: #fff;
  font-size: 10px;
  font-weight: 700;
}

.help-faq-question__icon {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  place-items: center;
  color: var(--help-blue);
  border-radius: 9px;
  background: rgba(var(--skin-primary-rgb), 0.08);
  font-size: 10px;
}

.help-faq-question > span:nth-child(2) {
  flex: 1 1 auto;
}

.help-faq-question__chevron {
  color: #98a3b2;
  font-size: 8px;
  transition: transform 160ms ease;
}

.help-faq-item--open .help-faq-question__chevron {
  color: var(--help-teal);
  transform: rotate(180deg);
}

.help-faq-answer {
  padding: 0 14px 12px 50px;
}

.help-faq-answer p {
  margin: 0;
  color: #687588;
  font-size: 9px;
  line-height: 1.65;
}

.help-support-card {
  position: sticky;
  top: 92px;
  text-align: center;
}

.help-support-card__icon {
  display: grid;
  width: 58px;
  height: 58px;
  margin: 0 auto 12px;
  place-items: center;
  color: #fff;
  border-radius: 18px;
  background: linear-gradient(135deg, var(--help-navy), var(--help-blue), var(--help-teal));
  box-shadow: 0 10px 24px rgba(var(--skin-primary-rgb), 0.2);
  font-size: 21px;
}

.help-support-card p {
  margin-bottom: 13px;
  line-height: 1.55;
}

.help-support-card ul {
  display: grid;
  gap: 7px;
  margin: 0 0 14px;
  padding: 11px;
  text-align: left;
  list-style: none;
  border: 1px solid var(--help-border);
  border-radius: 11px;
  background: #f8fafc;
}

.help-support-card li {
  color: #607086;
  font-size: 9px;
}

.help-support-card li i {
  width: 15px;
  color: var(--help-teal);
}

.help-support-card > a {
  display: flex;
  min-height: 38px;
  padding: 8px 12px;
  align-items: center;
  justify-content: space-between;
  color: #fff !important;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--help-navy), var(--help-blue), var(--help-teal));
  box-shadow: 0 7px 16px rgba(var(--skin-primary-rgb), 0.17);
  font-size: 10px;
  font-weight: 700;
}

.help-support-card > small {
  display: block;
  margin-top: 8px;
  color: #919cac !important;
  font-size: 8px;
  line-height: 1.5;
}

.help-empty-state {
  padding: 30px 16px;
  text-align: center;
  border: 1px dashed #d8e2ec;
  border-radius: 13px;
  background: #fafcfe;
}

.help-empty-state > span {
  display: grid;
  width: 42px;
  height: 42px;
  margin: 0 auto 9px;
  place-items: center;
  color: var(--help-teal);
  border-radius: 12px;
  background: rgba(var(--skin-secondary-rgb), 0.09);
}

.help-empty-state h3 {
  margin: 0 0 4px;
  color: #1a2433 !important;
  font-size: 13px !important;
}

.help-empty-state p {
  margin: 0 0 11px;
  color: #7b8798;
  font-size: 9px;
}

.help-empty-state button {
  min-height: 32px;
  padding: 6px 10px;
  color: #fff;
  border: 0;
  border-radius: 9px;
  background: var(--help-blue);
  font-size: 9px;
  font-weight: 700;
}

@media (max-width: 991.98px) {
  .help-center-hero {
    grid-template-columns: 1fr 230px;
  }

  .help-category-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .help-center-hero {
    grid-template-columns: 1fr;
  }

  .help-center-hero__content {
    padding: 22px 19px;
  }

  .help-center-hero__visual {
    display: none;
  }

  .help-center-layout {
    grid-template-columns: 1fr;
  }

  .help-support-card {
    position: static;
  }
}

@media (max-width: 479.98px) {
  .help-center-hero h1 {
    font-size: 23px !important;
  }

  .help-category-grid {
    grid-template-columns: 1fr;
  }
}
</style>
